import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ClipboardList, Save, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const DEFAULT_SUBJECTS = ["Français", "Mathématiques", "Histoire-Géo", "Sciences", "Anglais", "Arabe", "Éducation civique", "EPS", "Arts plastiques"];
const PERIODS = ["Trimestre 1", "Trimestre 2", "Trimestre 3"];

export default function GradesEntryPage() {
  const location = useLocation();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState(DEFAULT_SUBJECTS);
  const [className, setClassName] = useState("");
  const [students, setStudents] = useState([]);
  const [subject, setSubject] = useState("");
  const [period, setPeriod] = useState(PERIODS[0]);
  const [maxValue, setMaxValue] = useState(20);
  const [grades, setGrades] = useState({}); // {student_id: {value, comment}}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Classes + matières chargées automatiquement selon l'affectation (enseignant limité à son périmètre)
    api.get("/pedagogy/my-classes").then(async ({ data }) => {
      setClasses(data.classes || []);
      let subs = data.subjects || [];
      if (subs.length === 0) {
        // Staff ou enseignant sans matières affectées : matières actives paramétrées, sinon liste par défaut
        try {
          const { data: dbSubs } = await api.get("/pedagogy/subjects", { params: { active_only: true } });
          subs = dbSubs.length > 0 ? dbSubs.map(s => s.name) : DEFAULT_SUBJECTS;
        } catch { subs = DEFAULT_SUBJECTS; }
      }
      setSubjects(subs);
      setSubject(subs[0]);
      const preset = location.state?.class_name;
      if (preset && (data.classes || []).some(c => c.name === preset)) setClassName(preset);
      else if ((data.classes || []).length === 1) setClassName(data.classes[0].name);
    }).catch(() => toast.error("Erreur de chargement des classes"));
  }, []);

  useEffect(() => {
    if (!className) { setStudents([]); return; }
    setLoading(true);
    api.get("/pedagogy/class-students", { params: { class_name: className } })
      .then(({ data }) => setStudents(data))
      .catch(() => toast.error("Accès refusé à cette classe"))
      .finally(() => setLoading(false));
  }, [className]);

  const updateGrade = (sid, field, value) => {
    setGrades(g => ({ ...g, [sid]: { ...(g[sid] || {}), [field]: value } }));
  };

  const submit = async () => {
    const valid = students
      .filter(s => grades[s.id] && grades[s.id].value !== "" && grades[s.id].value !== undefined)
      .map(s => ({
        student_id: s.id,
        value: parseFloat(grades[s.id].value),
        comment: grades[s.id].comment || ""
      }))
      .filter(g => !isNaN(g.value) && g.value >= 0 && g.value <= maxValue);

    if (valid.length === 0) {
      toast.error("Aucune note valide à enregistrer");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/grades/bulk", {
        class_id: className, subject, period, max_value: maxValue, grades: valid
      });
      toast.success(`${data.created} note(s) enregistrée(s) pour ${subject}`);
      setGrades({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="page-grades-entry">
      <div>
        <div className="chip mb-2"><ClipboardList className="h-3 w-3" /><span>Saisie pédagogique</span></div>
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Saisie des notes</h2>
        <p className="text-sm text-slate-500 mt-1">Saisissez les notes pour toute une classe sur une matière donnée.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Classe</label>
            <Select value={className} onValueChange={setClassName}>
              <SelectTrigger className="w-full" data-testid="grades-class-select"><SelectValue placeholder="— Choisir —" /></SelectTrigger>
              <SelectContent>
                {classes.map(c => <SelectItem key={c.id} value={c.name}>{c.name}{c.level ? ` · ${c.level}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Matière</label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger className="w-full" data-testid="grades-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>{subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Période</label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-full" data-testid="grades-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Sur</label>
            <input type="number" min="1" max="100" value={maxValue} onChange={e => setMaxValue(parseFloat(e.target.value) || 20)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm h-9" data-testid="grades-max-input" />
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-10 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

      {!loading && className && students.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center text-sm text-yellow-800">
          Aucun élève inscrit dans cette classe pour le moment.
        </div>
      )}

      {students.length > 0 && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-[#1E3A8A]">{students.length} élève(s)</h3>
                <p className="text-xs text-slate-500">{subject} · {period} · /{maxValue}</p>
              </div>
              <Button onClick={submit} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="grades-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {students.map((s, i) => (
                <div key={s.id} className="p-4 grid grid-cols-12 gap-3 items-center hover:bg-slate-50" data-testid={`grade-row-${s.id}`}>
                  <div className="col-span-1 text-xs text-slate-400 font-mono">#{(i+1).toString().padStart(2,'0')}</div>
                  <div className={`col-span-1 w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs ${s.gender === "M" ? "bg-[#1E3A8A]" : "bg-[#EA580C]"}`}>
                    {s.first_name[0]}{s.last_name[0]}
                  </div>
                  <div className="col-span-5">
                    <div className="text-sm font-semibold text-[#0F172A]">{s.first_name} {s.last_name}</div>
                    <div className="text-xs text-slate-500">{s.gender === "M" ? "Garçon" : "Fille"} · {s.birth_date || "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="0" max={maxValue} step="0.25"
                        placeholder="—"
                        value={grades[s.id]?.value ?? ""}
                        onChange={e => updateGrade(s.id, "value", e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-center font-semibold focus:border-[#166534] focus:ring-2 focus:ring-[#166534]/20"
                        data-testid={`grade-value-${s.id}`}
                      />
                      <span className="text-xs text-slate-400 whitespace-nowrap">/{maxValue}</span>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      placeholder="Commentaire (optionnel)"
                      value={grades[s.id]?.comment || ""}
                      onChange={e => updateGrade(s.id, "comment", e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs"
                      data-testid={`grade-comment-${s.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="grades-save-btn-bottom">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Valider toutes les notes
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
